/* eslint-disable import/no-named-as-default-member */
import { Fragment, useRef, useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';

import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { getActiveBanners } from '@spotahome/marketplace-common/src/components/HomeCardBanner/config';
import HomeCardBanner from '@spotahome/marketplace-common/src/components/HomeCardBanner';

import Homecard from '@spotahome/marketplace-common/src/components/Homecard';

import IconLoading from '@spotahome/marketplace-common/src/LEGACY/javascripts/ui/icon/icon-loading';

import { useFiltersContext } from '@spotahome/ui-library/src/tenant/context/FiltersContext';
import { HomecardFormatter } from '@spotahome/ui-library/src/tenant/utils/images';
import { getDateAsUTC } from '@spotahome/ui-library/src/tenant/components/utils/dateUtils';
import { interseccion } from '@spotahome/ui-library/src/tenant/components/utils/arrayUtils';

import { getMonthlyPrice } from '@spotahome/marketplace-common/src/utils/homecards';
import useHomecardListTracking from '@spotahome/marketplace-common/src/utils/homecards-tracking';
import { useHighlightMarkersContext } from '@spotahome/marketplace-common/src/context/HighlightMarkersContext';

import { useSoyuzLocales } from '@spotahome/soyuz/client';

import { getCardsFromIds } from '../../utils/getCardsFromIds';

import AlertsCreationReminder from '../AlertsCreationReminder/AlertsCreationReminder';

import EmptyList from './EmptyList';

import './HomeCardList.scss';

const hasNewMarkers = (oldMarkers, newMarkers) =>
  JSON.stringify(oldMarkers.map(e => e.id)) !==
  JSON.stringify(newMarkers.map(e => e.id));

const scrollPageToTop = () => {
  document.querySelector('html').scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth'
  });
};

const HOMECARD_REQUEST_DELAY = 350;

export const HomeCardList = ({
  markerList,
  cityId,
  deviceType,
  initialHomecards = {},
  hasLazyLoad = false,
  hidePriceWithoutDates = false,
  showAlertCreationReminder = false,
  preloadedHomecards = [],
  hideBanners = false
}) => {
  const { filters } = useFiltersContext();
  const prevMarkers = useRef(markerList);
  const homecardListRef = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [homecards, setHomecards] = useState(initialHomecards);
  const {
    trackInitialMount,
    trackHomecardClick,
    trackProductImpressions,
    triggerHotjarRecording
  } = useHomecardListTracking(cityId);
  const { handleCardMouseEnter, handleCardMouseLeave } =
    useHighlightMarkersContext();
  const { current: currentLocale } = useSoyuzLocales();

  const currentYear = getDateAsUTC(new Date()).getFullYear();

  useEffect(() => {
    trackInitialMount(markerList, homecards);
  }, []);

  useEffect(() => {
    const requestController = new AbortController();
    let debouncedTimer;
    const requestNewHomecards = async () => {
      setIsLoading(true);

      const ids = markerList.map(card => card.id);
      const newHomecards = await getCardsFromIds(ids, currentLocale, {
        fetchOptions: {
          signal: requestController.signal
        }
      });
      trackProductImpressions(markerList, newHomecards);
      triggerHotjarRecording(homecards);

      setHomecards(newHomecards);
      prevMarkers.current = markerList;

      setIsLoading(false);
    };

    if (hasNewMarkers(prevMarkers.current, markerList)) {
      scrollPageToTop();
      debouncedTimer = setTimeout(
        () => requestNewHomecards(),
        HOMECARD_REQUEST_DELAY
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps

    return () => {
      requestController.abort();
      clearTimeout(debouncedTimer);
    };
  }, [markerList]);

  const homecardKeys = Object.keys(homecards);

  useEffect(() => {
    const numberOfAreaHomecards = homecardKeys.filter(
      key => homecards[key].area || homecards[key].roomArea
    ).length;

    if (numberOfAreaHomecards > 0) {
      SoyuzAnalytics.sendGA4Event('Search', {
        section: 'meters-homecards',
        value: numberOfAreaHomecards,
        number_of_items: homecardKeys.length
      });
    }

    const numberOfMultilistingHomecards = homecardKeys.filter(
      key => homecards[key].totalCountMultilisting > 0
    ).length;

    if (numberOfMultilistingHomecards > 0) {
      SoyuzAnalytics.sendGA4Event('Search', {
        section: 'multilisting-homecards',
        value: numberOfMultilistingHomecards,
        number_of_items: homecardKeys.length
      });
    }
  }, [JSON.stringify(homecardKeys)]);

  const handleHomecardImageChange = (action, { index }) => {
    SoyuzAnalytics.sendGA4Event(`homecard-image-slide`, { action, index });
  };

  const renderHomecardList = () => {
    const shouldShowAvailableFrom = !filters['move-in'];

    const shouldShowPrice = !(hidePriceWithoutDates && !filters['move-in']);
    const [activeBanner] = getActiveBanners({
      showValuePropositionBanner: false
    });

    const availableHomecards = markerList.filter(({ id }) => homecards[id]);

    return availableHomecards
      .map((homecard, position) => {
        const cardData = homecards[homecard.id];

        const data = {
          ...cardData,
          photos: HomecardFormatter.getPhotos(
            cardData.photoIds,
            cardData.otherPropertyPhotoIds
          ),
          photoUrls: HomecardFormatter.getPhotoUrls(cardData.photoIds),
          monthlyPrice: getMonthlyPrice(cardData.displayPrice)
        };

        const firstAvailableDate = getDateAsUTC(
          new Date(data.firstAvailableDate)
        );
        const firstAvailableYear = firstAvailableDate.getFullYear();

        return (
          <Fragment key={`homecard-${homecard.id}`}>
            {!hideBanners &&
            activeBanner &&
            activeBanner.position === position ? (
              <HomeCardBanner
                className="l-list__item"
                name={activeBanner.name}
                component={activeBanner.component}
                bannerConfig={activeBanner.componentConfig}
                bannerTracking={activeBanner.tracking}
                cookieExpiration={activeBanner.cookieExpiration}
                isOneColumnView
              />
            ) : null}

            <div className="l-list__item" data-homecard-scroll={homecard.id}>
              <Homecard
                isShowStatus
                data={data}
                position={position}
                moveIn={filters['move-in']}
                moveOut={filters['move-out']}
                flexibleMoveOut={!!filters.flexibleMoveOut}
                flexibleMoveIn={!!filters.flexibleMoveIn}
                onCardMouseEnter={handleCardMouseEnter}
                onCardMouseLeave={handleCardMouseLeave}
                onClick={() => {
                  trackHomecardClick(position, data);
                }}
                reviews={data.reviews}
                hasLazyLoad={
                  preloadedHomecards.includes(data.id) ? false : hasLazyLoad
                }
                showPrice={shouldShowPrice}
                newTab={
                  !(deviceType === 'smartphone' || deviceType === 'tablet')
                }
                showAvailableFromYear={
                  shouldShowAvailableFrom && firstAvailableYear !== currentYear
                }
                pageType="search"
                showDescription
                onImageChange={handleHomecardImageChange}
                isBooked={homecard.so}
                showSquaredMeters
              />
            </div>
          </Fragment>
        );
      })
      .filter(Boolean);
  };

  const shouldShowEmptyList = () => {
    const markersIds = markerList.map(marker => marker.id);
    const homecardIds = Object.keys(homecards).map(homecardId =>
      parseInt(homecardId, 10)
    );

    const homecardIdsMarkerIdsIntersection = interseccion(
      markersIds,
      homecardIds
    );

    const notMatchingMarkersAndHomecards =
      homecardIdsMarkerIdsIntersection.length !== markersIds.length;

    return (
      !isLoading && notMatchingMarkersAndHomecards && markerList?.length > 0
    );
  };

  const hasHomecards = markerList?.length && Object.keys(homecards).length;

  return (
    <div
      data-cy="property-list"
      data-test="property-list"
      className="l-list"
      ref={homecardListRef}
    >
      {isLoading || shouldShowEmptyList() ? <EmptyList /> : null}
      {!isLoading && !shouldShowEmptyList() && hasHomecards
        ? renderHomecardList()
        : null}
      {!isLoading && showAlertCreationReminder ? (
        <AlertsCreationReminder cityId={cityId} />
      ) : null}
      <div className="spinner-loading-map" hidden={!isLoading}>
        <IconLoading />
      </div>
    </div>
  );
};

HomeCardList.propTypes = {
  markerList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  initialHomecards: PropTypes.shape({}),
  cityId: PropTypes.string.isRequired,
  hasLazyLoad: PropTypes.bool,
  hidePriceWithoutDates: PropTypes.bool,
  showAlertCreationReminder: PropTypes.bool,
  preloadedHomecards: PropTypes.arrayOf(PropTypes.string),
  deviceType: PropTypes.string.isRequired,
  hideBanners: PropTypes.bool
};

export default memo(HomeCardList, (prevProps, nextProps) => {
  return (
    JSON.stringify(prevProps.markerList) ===
      JSON.stringify(nextProps.markerList) &&
    prevProps.showAlertCreationReminder === nextProps.showAlertCreationReminder
  );
});
